/**
 * SF-4758: the combined ranking. sf-api answers 403 NOT_SUBMITTED until the caller has saved a
 * ranking with at least one item (insights users always see it), so a person ranks before they
 * see what everyone else ranked.
 */
import { useState } from 'react';
import { Alert, Select, Skeleton, Space, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { TEAM_OPTIONS, type TeamKey } from '../../../utils/teams';
import { useParticipation, useResults, type ResultItem } from '../api/prioritiesClient';
import { LoadError, RefreshWarning } from './LoadError';
import { JiraLinks } from './JiraLinks';
import { WantedByCompanies } from './WantedByCompanies';

export const NOT_SUBMITTED_MESSAGE = 'Save your ranking to see the combined results';

const EVERYONE = 'everyone';
const TEAM_FILTER_OPTIONS = [{ value: EVERYONE, label: 'Everyone' }, ...TEAM_OPTIONS];

const columns: ColumnsType<ResultItem> = [
  { title: 'Rank', dataIndex: 'rank', width: 80 },
  {
    title: 'Item',
    dataIndex: 'title',
    // SF-5104: which companies want it, shown only (it never changes the ranking).
    render: (title: string, item) =>
      item.jiraLinks?.length || item.companies?.length ? (
        <Space size={4} wrap>
          {title}
          <JiraLinks links={item.jiraLinks} />
          <WantedByCompanies companies={item.companies} />
        </Space>
      ) : (
        title
      ),
  },
  { title: 'Points', dataIndex: 'points', width: 100 },
  {
    title: 'Ranked by',
    dataIndex: 'voterCount',
    width: 140,
    render: (count: number) => `ranked by ${count}`,
  },
];

/** "X of Y people have ranked", shown to everyone who can open the page. */
export const ParticipationLine: React.FC<{ listId: string }> = ({ listId }) => {
  const query = useParticipation(true, listId);
  const counts = query.data?.data;
  // Never loaded: say so, with a Retry, rather than leaving the counts out silently.
  if (!counts) {
    return query.isError ? (
      <LoadError
        error={query.error}
        fallback="The ranking counts could not be loaded."
        onRetry={query.refetch}
      />
    ) : null;
  }
  // A failed refetch (every write refreshes every priorities read) keeps the last counts, but
  // says they may be out of date rather than passing them off as current.
  return (
    <>
      <Typography.Text type="secondary">
        {counts.submitted} of {counts.eligible} people have ranked
      </Typography.Text>
      {query.isError && (
        <RefreshWarning
          error={query.error}
          fallback="The ranking counts could not be refreshed."
          onRetry={query.refetch}
        />
      )}
    </>
  );
};

export const ResultsTab: React.FC<{ canInsights: boolean; listId: string }> = ({
  canInsights,
  listId,
}) => {
  const [team, setTeam] = useState<TeamKey | null>(null);
  const query = useResults(team, true, listId);
  // The latest answer wins: a refusal (no saved ranking) hides results even when a later refresh
  // fails, and a refreshed results answer shows them again.
  const notSubmitted = query.data?.kind === 'notSubmitted';
  const results = query.data?.kind === 'results' ? query.data.results : undefined;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space wrap>
        <ParticipationLine listId={listId} />
        {canInsights && (
          <Select
            aria-label="Team"
            style={{ minWidth: 200 }}
            value={team ?? EVERYONE}
            options={TEAM_FILTER_OPTIONS}
            onChange={(value: string) => setTeam(value === EVERYONE ? null : (value as TeamKey))}
          />
        )}
      </Space>
      {notSubmitted ? (
        <>
          {/* The last answer was "no saved ranking"; a refresh since then failed. */}
          {query.isError && (
            <RefreshWarning
              error={query.error}
              fallback="The results could not be refreshed."
              onRetry={query.refetch}
            />
          )}
          <Alert type="info" showIcon message={NOT_SUBMITTED_MESSAGE} />
        </>
      ) : query.isError && !results ? (
        <LoadError
          error={query.error}
          fallback="The results could not be loaded."
          onRetry={query.refetch}
        />
      ) : query.isLoading ? (
        // No answer yet (first load, or a ranking save dropped the cache): never an old table.
        <Skeleton active />
      ) : (
        <>
          {/* Only a real failure (network, 500) of a refetch gets here with an error: the
              cached table stays, with a warning. */}
          {query.isError && (
            <RefreshWarning
              error={query.error}
              fallback="The results could not be refreshed."
              onRetry={query.refetch}
            />
          )}
          <Table<ResultItem>
            rowKey="id"
            columns={columns}
            dataSource={results?.items ?? []}
            pagination={false}
            locale={{ emptyText: 'No open items yet' }}
          />
        </>
      )}
    </Space>
  );
};
